/**
 * ensureTokens — put the suite palette on the page if nobody else brought it.
 *
 * Every embed renders into Shadow DOM, and custom properties DO cross that
 * boundary: they are inherited, and `all:initial` on :host does not reset them
 * (the CSS-wide keywords deliberately exclude custom properties). Verified in
 * Chrome against <semi360-loader>, whose :host does exactly that — the disc
 * still resolved var(--brand-ink) to #191334 inside the shadow root.
 *
 * What does NOT cross is a palette that was never on the page. Embeds used to
 * cover that with `var(--token, #literal)` fallbacks, which the unified CSS
 * standard rules out for good reason: a fallback that drifts from the token is
 * worse than none, because it hides the drift and reverts silently on a rename.
 * So the embed brings the tokens instead of guessing them, and its CSS reads
 * bare var().
 *
 * Lifted verbatim out of footer.ts, which was the only embed doing it, so the
 * dedupe rules stay one implementation rather than eight.
 */

/** The id the tokens link takes. A page that already linked tokens.css (the
 *  Hub's own pages, the demos) keeps its own; nothing is injected twice. */
const TOKENS_ID = "semi360-tokens";

/**
 * Products link tokens.css two different ways today: bom360 gives its link
 * the id "semi360-tokens", xcess360 links it bare, with no id at all. Dedupe
 * on the id OR on any existing stylesheet link whose href already ends in
 * "/embed/tokens.css" — otherwise the bare-link products would get a second,
 * redundant copy.
 */
export function ensureTokens(base: string): void {
  try {
    if (document.getElementById(TOKENS_ID)) return;
    const links = document.querySelectorAll('link[rel="stylesheet"]');
    for (const l of Array.from(links)) {
      const href = (l as HTMLLinkElement).getAttribute("href") || "";
      if (href.replace(/[?#].*$/, "").endsWith("/embed/tokens.css")) return;
    }
    const link = document.createElement("link");
    link.id = TOKENS_ID;
    link.rel = "stylesheet";
    link.href = `${base}/embed/tokens.css`;
    // First, not last: a product's own :root must still win for any token
    // name they share.
    const h = document.head || document.documentElement;
    h.insertBefore(link, h.firstChild);
  } catch {
    /* a page that will not take the link keeps whatever palette it has */
  }
}
